"use client";

import { mainSidebarLinks } from "@/data/nav";
import { useGetUser, useLogout } from "@/hooks/main-hook";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "react-icons/ri";
import MainLoading from "./MainLoading";
import MainNavigation from "./Navigation";
import UserCard from "./UserCard";

/* ─────────────────────────────────────────────────────────────
   Premium dark sidebar — deep navy with gold accents.
   Shared by developer / agent / marketer / bank portals.
   ───────────────────────────────────────────────────────────── */
export default function Sidebar() {
  const locale = useLocale();
  const pathname = usePathname();
  const navigate = useRouter();
  const [show, setShow] = useState(true);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const { mutate: logout, isPending }: any = useLogout();
  const { data } = useGetUser();
  const user = data?.user;

  const isActive = pathname.includes("/dashboard") || pathname.includes("/bank");
  const isVerified = user?.is_bank_registered || false;
  const role = user?.role;
  const ar = locale === "ar";

  const handleLogout = () => {
    logout(
      { userId: user?.id, role: "developer" },
      { onSuccess: () => navigate.push(`/${locale}`) },
    );
  };

  useEffect(() => {
    mainSidebarLinks.forEach((link) => {
      if (link.subMenu?.some((sub) => pathname.startsWith(sub.href))) {
        setOpenMenu(link.name_en);
      }
    });
  }, [pathname]);

  if (pathname === `/${locale}` || pathname.includes("/auth")) return null;
  if (!isActive && !user) return null;
  if (role === "bank" && !isVerified) return null;

  const width = show ? "w-72" : "w-[84px]";

  return (
    <aside className={`h-screen sticky top-0 shrink-0 transition-all duration-300 ${width}`}>
      <div
        className={`fixed top-0 h-screen z-30 transition-all duration-300 ease-in-out ${width}
          bg-[#0a1128] text-white
          ${ar ? "border-l" : "border-r"} border-white/[0.06]
          shadow-[0_0_60px_-20px_rgba(13,53,137,0.6)]`}
      >
        {/* ambient glows */}
        <div className="pointer-events-none absolute top-0 right-0 size-56 rounded-full bg-[#0d3589]/40 blur-3xl" />
        <div className="pointer-events-none absolute bottom-24 left-0 size-48 rounded-full bg-[#d9b25a]/10 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9b25a]/60 to-transparent" />

        <div className="relative flex h-full flex-col overflow-hidden">
          {/* logo */}
          <div className="flex h-20 items-center justify-center border-b border-white/[0.06] px-5">
            <Link href={pathname.includes("/bank") ? "/bank/dashboard" : "/dashboard"} className="flex items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/95 shadow-[0_0_24px_rgba(217,178,90,0.25)]">
                <Image src="/sna.png" alt="SNA" width={26} height={26} className="object-contain" priority />
              </span>
              {show && (
                <span className="overflow-hidden">
                  <span className="block text-[15px] font-black leading-tight tracking-tight">SNA</span>
                  <span className="block text-[9px] font-bold uppercase tracking-[2px] text-[#d9b25a]/80">
                    {role === "bank" ? (ar ? "بوابة البنوك" : "Bank Portal") : ar ? "بوابة الشركاء" : "Partner Portal"}
                  </span>
                </span>
              )}
            </Link>
          </div>

          {/* nav */}
          <div className="flex-1 overflow-y-auto px-3 py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {show && (
              <div className="mb-3 flex items-center gap-2 px-2">
                <span className="text-[9px] font-black uppercase tracking-[2.5px] text-white/25">
                  {ar ? "القائمة" : "Menu"}
                </span>
                <span className="h-px flex-1 bg-white/[0.06]" />
              </div>
            )}
            <MainNavigation
              pathname={pathname}
              locale={locale}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              show={show}
              userRole={user?.role}
            />
          </div>

          {/* user */}
          <div className="border-t border-white/[0.06] p-3">
            <UserCard user={user} show={show} locale={locale} isVerified={isVerified} handleLogout={handleLogout} />
          </div>
        </div>

        {/* collapse toggle */}
        <button
          onClick={() => setShow(!show)}
          className={`absolute top-[26px] ${ar ? "-left-3.5" : "-right-3.5"} z-50 grid size-7 place-items-center rounded-full border-2 border-[#0a1128] bg-gradient-to-br from-[#d9b25a] to-[#b8923f] text-[#0a1128] shadow-[0_0_16px_rgba(217,178,90,0.5)] transition-transform hover:scale-110`}
          aria-label="toggle sidebar"
        >
          {show ? (ar ? <RiArrowRightSLine /> : <RiArrowLeftSLine />) : ar ? <RiArrowLeftSLine /> : <RiArrowRightSLine />}
        </button>
      </div>

      {isPending && <MainLoading />}
    </aside>
  );
}
